/**
 * Custom hook to sync borrower draft data with Redux store
 * Consolidates all Redux dispatch operations for draft loading/saving
 */

import { useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  setBusinessDoc,
  setProofDoc,
  setShareDoc,
  setBankDoc,
  setBalance,
  setCertificateOfIncorporationDoc,
  setSupportingDocumentsWithSuppliersDoc,
} from "../../../features/merchant/doc";
import { hydrateBizState } from "../../../features/merchant/biz";
import { hydrateRepState } from "../../../features/merchant/rep";
import { hydrateBankState } from "../../../features/merchant/bankSlice";
import {
  setUserFirstName,
  setUserLastName,
} from "@funded-here-interface/common/src/features/auth/authSlice_Investor";

export interface BorrowerDraftData {
  // Documents
  proofOfAddress?: any[];
  businessProfile?: any[];
  shareHoldersDirectorsRegisters?: any[];
  bankStatement?: any[];
  balanceSheetPAndLStatement?: any[];
  certificateOfIncorporation?: any[];
  supportingDocumentsWithSuppliers?: any[];

  // Business info
  companyName?: string;
  companyRegistrationNo?: string;
  legalStructure?: string;
  countryOfIncorporation?: string;
  countryOfDomicile?: string;
  region?: string;
  city?: string;
  legalEntityAddress?: string;
  postalCode?: string;
  website?: string;
  phoneNumber?: string;
  companyStartDate?: string;
  industryGroup?: string;
  industry?: string;

  // Representative info
  repFirstName?: string;
  repLastName?: string;
  userFirstName?: string;
  userLastName?: string;
  repEmail?: string;
  repTitle?: string;
  merchantTitle?: string;
  shareHoldersDirectors?: any[];

  // Bank info
  bankName?: string;
  bankCode?: string;
  accountType?: string;
  accountNumber?: string;
  accountHolderName?: string;
  swiftCode?: string;

  // Onboarding status
  onboardingStatus?: string;
}

/**
 * Hook to sync borrower draft data with Redux store
 */
export const useBorrowerDraftSync = () => {
  const dispatch = useDispatch();

  /**
   * Sync all draft data to Redux store
   * Uses hydrate pattern for efficient bulk updates
   */
  const syncBorrowerDraftToRedux = useCallback(
    (data: BorrowerDraftData) => {
      // Sync document uploads - Keep individual dispatches as doc slice uses array setters
      dispatch(setProofDoc(data.proofOfAddress || []));
      dispatch(setBusinessDoc(data.businessProfile || []));
      dispatch(setShareDoc(data.shareHoldersDirectorsRegisters || []));
      dispatch(setBankDoc(data.bankStatement || []));
      dispatch(setBalance(data.balanceSheetPAndLStatement || []));
      dispatch(
        setCertificateOfIncorporationDoc(data.certificateOfIncorporation || [])
      );
      dispatch(
        setSupportingDocumentsWithSuppliersDoc(
          data.supportingDocumentsWithSuppliers || []
        )
      );

      // Sync business info - Single dispatch with hydrateBizState
      const formattedDate = data.companyStartDate
        ? new Date(data.companyStartDate).toISOString().split("T")[0]
        : null;

      dispatch(
        hydrateBizState({
          company: data.companyName ?? null,
          regNumber: data.companyRegistrationNo ?? null,
          legalStructure: data.legalStructure ?? null,
          countryOfIncorp: data.countryOfIncorporation ?? null,
          countryOfDomicile: data.countryOfDomicile ?? null,
          region: data.region ?? null,
          city: data.city ?? null,
          address: data.legalEntityAddress ?? null,
          postal: data.postalCode ?? null,
          website: data.website ?? null,
          phoneNumber: data.phoneNumber ?? null,
          incorporationDate: formattedDate,
          industryGroup: data.industryGroup ?? null,
          typeOfInstitute: data.industry ?? null,
        })
      );

      // Sync representative info - Single dispatch with hydrateRepState
      dispatch(
        hydrateRepState({
          repFirstName: data.repFirstName ?? null,
          repLastName: data.repLastName ?? null,
          repEmail: data.repEmail ?? null,
          repTitle: data.repTitle ?? null,
          merchantTitle: data.merchantTitle ?? null,
          shareholdersDirectors: data.shareHoldersDirectors || [],
        })
      );

      // Sync user info (auth slice - uses wrapped payload pattern)
      dispatch(setUserFirstName({ userFirstName: data.userFirstName ?? null }));
      dispatch(setUserLastName({ userLastName: data.userLastName ?? null }));

      // Sync bank info - Single dispatch with hydrateBankState
      dispatch(
        hydrateBankState({
          bankName: data.bankName ?? null,
          bankCode: data.bankCode ?? null,
          accountType: data.accountType ?? null,
          accountNumber: data.accountNumber ?? null,
          accountHolderName: data.accountHolderName ?? null,
          swiftCode: data.swiftCode ?? null,
        })
      );
    },
    [dispatch]
  );

  /**
   * Reset documents to initial draft data or empty arrays
   */
  const resetBorrowerDocuments = useCallback(
    (initialData?: BorrowerDraftData) => {
      const resetData = initialData || {
        proofOfAddress: [],
        businessProfile: [],
        shareHoldersDirectorsRegisters: [],
        bankStatement: [],
        balanceSheetPAndLStatement: [],
        certificateOfIncorporation: [],
        supportingDocumentsWithSuppliers: [],
      };

      dispatch(setProofDoc(resetData.proofOfAddress || []));
      dispatch(setBusinessDoc(resetData.businessProfile || []));
      dispatch(setShareDoc(resetData.shareHoldersDirectorsRegisters || []));
      dispatch(setBankDoc(resetData.bankStatement || []));
      dispatch(setBalance(resetData.balanceSheetPAndLStatement || []));
      dispatch(
        setCertificateOfIncorporationDoc(
          resetData.certificateOfIncorporation || []
        )
      );
      dispatch(
        setSupportingDocumentsWithSuppliersDoc(
          resetData.supportingDocumentsWithSuppliers || []
        )
      );
    },
    [dispatch]
  );

  return { syncBorrowerDraftToRedux, resetBorrowerDocuments };
};
